// src/services/wordleService.js
import { db } from '../firebase';
import { doc, getDoc, collection, getDocs, setDoc, arrayUnion, arrayRemove } from 'firebase/firestore';

/**
 * Fetches all Wordle puzzles from Firestore
 * Handles both old format (individual documents) and new format (year documents with maps)
 * @returns {Promise<Array>} Array of puzzle objects sorted by date
 */
export const fetchWordlePuzzles = async () => {
  try {
    const snapshot = await getDocs(collection(db, 'wordle-puzzles'));
    const puzzleMap = new Map();

    snapshot.docs.forEach(doc => {
      const docId = doc.id;

      // Check if this is a year document (4 digits)
      if (docId.length === 4 && !isNaN(docId)) {
        // New format: year document with map of day-month keys
        const year = docId;
        const data = doc.data();

        Object.entries(data).forEach(([dayMonth, word]) => {
          const fullDate = `${dayMonth}-${year}`; // "24-01-2026"
          puzzleMap.set(fullDate, { id: fullDate, word });
        });
      } else {
        // Old format: individual document with full date as ID
        const data = doc.data();
        puzzleMap.set(docId, { id: docId, ...data });
      }
    });

    // Convert map to sorted array
    const list = Array.from(puzzleMap.values()).sort((a, b) => a.id.localeCompare(b.id));
    return list;
  } catch (error) {
    console.error('Failed to fetch Wordle puzzles:', error);
    throw error;
  }
};

/**
 * Saves a Wordle puzzle to Firestore
 * Writes to BOTH old and new formats during transition period for backward compatibility
 * @param {Object} puzzleData - Puzzle object with id (DD-MM-YYYY) and word properties
 * @returns {Promise<void>}
 */
export const saveWordlePuzzle = async (puzzleData) => {
  try {
    // Parse date: "dd-MM-yyyy" -> year and "dd-MM" key
    const parts = puzzleData.id.split('-');
    const year = parts[2]; // "2026"
    const dayMonth = `${parts[0]}-${parts[1]}`; // "24-01"

    // Write to BOTH formats during transition period

    // 1. Old format (individual document) - for backward compatibility
    await setDoc(doc(db, 'wordle-puzzles', puzzleData.id), puzzleData);

    // 2. New format (year document with map)
    const yearDocRef = doc(db, 'wordle-puzzles', year);
    const yearDoc = await getDoc(yearDocRef);

    const currentData = yearDoc.exists() ? yearDoc.data() : {};
    currentData[dayMonth] = puzzleData.word;

    await setDoc(yearDocRef, currentData);

    console.log('Wordle puzzle saved successfully (both formats)');
  } catch (error) {
    console.error('Failed to save Wordle puzzle:', error);
    throw error;
  }
};

/**
 * Fetches all Quordle puzzles from Firestore
 * Uses ONLY the new yearly format (quordle-{year} documents)
 * @returns {Promise<Array>} Array of puzzle objects sorted by date
 */
export const fetchQuordlePuzzles = async () => {
  try {
    const snapshot = await getDocs(collection(db, 'wordle-puzzles'));
    const puzzleMap = new Map();

    snapshot.docs.forEach(doc => {
      const docId = doc.id;

      // Only process quordle-{year} documents
      if (docId.startsWith('quordle-')) {
        const year = docId.substring(8); // Remove 'quordle-' prefix
        const data = doc.data();

        Object.entries(data).forEach(([dayMonth, words]) => {
          const fullDate = `${dayMonth}-${year}`; // "01-01-2026"
          puzzleMap.set(fullDate, { id: fullDate, words });
        });
      }
    });

    const list = Array.from(puzzleMap.values()).sort((a, b) => a.id.localeCompare(b.id));
    return list;
  } catch (error) {
    console.error('Failed to fetch Quordle puzzles:', error);
    throw error;
  }
};

/**
 * Saves a Quordle puzzle to Firestore
 * Uses ONLY the new yearly format (no backwards compatibility needed)
 * @param {Object} puzzleData - Puzzle object with id (DD-MM-YYYY) and words (space-separated string or array)
 * @returns {Promise<void>}
 */
export const saveQuordlePuzzle = async (puzzleData) => {
  try {
    const parts = puzzleData.id.split('-');
    const year = parts[2]; // "2026"
    const dayMonth = `${parts[0]}-${parts[1]}`; // "01-01"

    // Only use new format: quordle-{year} document
    const yearDocRef = doc(db, 'wordle-puzzles', `quordle-${year}`);
    const yearDoc = await getDoc(yearDocRef);

    const currentData = yearDoc.exists() ? yearDoc.data() : {};
    currentData[dayMonth] = puzzleData.words; // Store as-is (string or will be converted to string)

    await setDoc(yearDocRef, currentData);

    console.log('Quordle puzzle saved successfully');
  } catch (error) {
    console.error('Failed to save Quordle puzzle:', error);
    throw error;
  }
};

// Words the admin has disliked; never picked when randomizing Wordle or Quordle words.
const dislikedRef = () => doc(db, 'wordle-admin', 'disliked');

export const fetchDislikedWords = async () => {
  const snap = await getDoc(dislikedRef());
  return new Set(snap.exists() ? snap.data().words || [] : []);
};

export const addDislikedWord = async (word) => {
  await setDoc(dislikedRef(), { words: arrayUnion(word) }, { merge: true });
};

export const removeDislikedWord = async (word) => {
  await setDoc(dislikedRef(), { words: arrayRemove(word) }, { merge: true });
};
